import { useState, useEffect, useRef, useCallback } from 'react';
import { captureEvent } from '../posthogClient';
import { isIOS, isStandaloneDisplayMode } from '../utils/pwaUtils';
import {
  getDeferredInstallPrompt,
  setDeferredInstallPrompt,
  clearDeferredInstallPrompt,
} from '../utils/deferredInstallPromptStore';

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

function showIOSInstructions() {
  window.alert(
    'Untuk menginstall aplikasi di iOS:\n\n' +
      '1. Tap tombol Share di Safari\n' +
      '2. Pilih "Add to Home Screen"\n' +
      '3. Tap "Add" untuk menginstall'
  );
}

function showAndroidInstructions() {
  window.alert(
    'Install belum tersedia otomatis saat ini.\n\n' +
      'Di Android Chrome:\n' +
      '1. Tap menu Chrome (⋮)\n' +
      '2. Pilih "Install app" atau "Add to Home screen"\n' +
      '3. Konfirmasi install'
  );
}

/**
 * State and handlers for the floating PWA install dialog (F20).
 * @param {{ analyticsEnabled: boolean }} opts — when true, also captures `pwa_install_click` (funnel).
 */
export function usePWAInstallPrompt({ analyticsEnabled }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const promptRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    const alreadyInstalled = () => isStandaloneDisplayMode();

    const checkIfInstalled = () => {
      if (alreadyInstalled()) {
        setIsInstalled(true);
        return;
      }
      const dismissedTime = localStorage.getItem('pwa-prompt-dismissed');
      if (dismissedTime) {
        const dismissedDate = new Date(dismissedTime);
        const daysSinceDismissed =
          (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissed < 30) {
          return;
        }
      }
    };

    checkIfInstalled();
    const sharedDeferredPrompt = getDeferredInstallPrompt();
    if (sharedDeferredPrompt) {
      setDeferredPrompt(sharedDeferredPrompt);
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setDeferredInstallPrompt(e);
      const dismissedTime = localStorage.getItem('pwa-prompt-dismissed');
      if (!dismissedTime && !alreadyInstalled()) {
        setTimeout(() => {
          if (!alreadyInstalled()) {
            captureEvent('pwa_install_prompted', { source: 'beforeinstallprompt' });
            setShowPrompt(true);
          }
        }, 30000);
      }
    };

    const dismissedTime = localStorage.getItem('pwa-prompt-dismissed');
    if ((isIOS() || !window.deferredPrompt) && !dismissedTime && !alreadyInstalled()) {
      setTimeout(() => {
        if (!alreadyInstalled()) {
          captureEvent('pwa_install_prompted', { source: 'ios_fallback' });
          setShowPrompt(true);
        }
      }, 30000);
    }

    const handleAppInstalled = () => {
      captureEvent('pwa_install_accepted', { source: 'appinstalled' });
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
      clearDeferredInstallPrompt();
      localStorage.setItem('pwa-installed', 'true');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = useCallback(async () => {
    if (analyticsEnabled) {
      captureEvent('pwa_install_click', { source: 'dialog_primary' });
    }

    if (!deferredPrompt) {
      if (isIOS()) {
        showIOSInstructions();
      } else {
        captureEvent('pwa_install_unavailable', { source: 'dialog_primary' });
        showAndroidInstructions();
      }
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      captureEvent('pwa_install_accepted', { source: 'deferred_prompt' });
      setIsInstalled(true);
      localStorage.setItem('pwa-installed', 'true');
    } else {
      captureEvent('pwa_install_dismissed', { source: 'deferred_prompt', outcome });
      localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString());
    }

    setDeferredPrompt(null);
    clearDeferredInstallPrompt();
    setShowPrompt(false);
  }, [deferredPrompt, analyticsEnabled]);

  const handleDismiss = useCallback(() => {
    captureEvent('pwa_install_dismissed', { source: 'close_button' });
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString());
  }, []);

  useEffect(() => {
    if (!showPrompt) return undefined;

    previousFocusRef.current = document.activeElement;
    const el = promptRef.current;
    const focusables = el ? Array.from(el.querySelectorAll(FOCUSABLE_SELECTOR)) : [];

    if (focusables.length > 0) focusables[0].focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleDismiss();
        return;
      }
      if (e.key !== 'Tab' || focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (previousFocusRef.current?.focus) previousFocusRef.current.focus();
    };
  }, [showPrompt, handleDismiss]);

  return {
    showPrompt,
    isInstalled,
    promptRef,
    handleInstallClick,
    handleDismiss,
  };
}
